import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import ImageUploader from "@/components/admin/ImageUploader";
import GalleryUploader from "@/components/admin/GalleryUploader";
import RichTextEditor from "@/components/admin/RichTextEditor";
import MultiAthletePicker from "@/components/admin/MultiAthletePicker";
import { publicUrl } from "@/lib/storage";
import { toast } from "sonner";

type Team = {
  id: string;
  slug: string;
  name: string;
  sport: string | null;
  description: string | null;
  logo_path: string | null;
  image_paths: string[];
  video_url: string | null;
  website_url: string | null;
  tickets_url: string | null;
  shop_url: string | null;
  instagram_url: string | null;
  tiktok_url: string | null;
  youtube_url: string | null;
  published: boolean;
  sort_order: number;
};

type Editing = Partial<Team> & { id?: string; athleteIds: string[] };

const empty = (): Editing => ({
  slug: "",
  name: "",
  sport: "",
  description: "",
  logo_path: null,
  image_paths: [],
  video_url: "",
  website_url: "",
  tickets_url: "",
  shop_url: "",
  instagram_url: "",
  tiktok_url: "",
  youtube_url: "",
  published: false,
  sort_order: 0,
  athleteIds: [],
});

const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const AdminTeams = () => {
  const [list, setList] = useState<Team[]>([]);
  const [athletes, setAthletes] = useState<{ id: string; name: string }[]>([]);
  const [links, setLinks] = useState<Record<string, string[]>>({});
  const [editing, setEditing] = useState<Editing | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [t, a, at] = await Promise.all([
      supabase.from("teams").select("*").order("sort_order").order("created_at", { ascending: false }),
      supabase.from("athletes").select("id,name").order("name"),
      supabase.from("athlete_teams").select("team_id, athlete_id"),
    ]);
    if (t.error) toast.error(t.error.message);
    setList((t.data as any) ?? []);
    setAthletes((a.data as any) ?? []);
    const map: Record<string, string[]> = {};
    ((at.data as any) ?? []).forEach((row: any) => {
      (map[row.team_id] ||= []).push(row.athlete_id);
    });
    setLinks(map);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const startEdit = (t: Team) => setEditing({ ...t, athleteIds: links[t.id] ?? [] });

  const save = async () => {
    if (!editing) return;
    if (!editing.name) { toast.error("Team name required"); return; }
    if ((editing.image_paths ?? []).length > 5) { toast.error("Max 5 images"); return; }

    const payload: any = {
      slug: editing.slug || slugify(editing.name),
      name: editing.name,
      sport: editing.sport || null,
      description: editing.description || null,
      logo_path: editing.logo_path ?? null,
      image_paths: editing.image_paths ?? [],
      video_url: editing.video_url || null,
      website_url: editing.website_url || null,
      tickets_url: editing.tickets_url || null,
      shop_url: editing.shop_url || null,
      instagram_url: editing.instagram_url || null,
      tiktok_url: editing.tiktok_url || null,
      youtube_url: editing.youtube_url || null,
      published: !!editing.published,
      sort_order: editing.sort_order ?? 0,
    };

    let teamId = editing.id;
    if (teamId) {
      const { error } = await supabase.from("teams").update(payload).eq("id", teamId);
      if (error) return toast.error(error.message);
    } else {
      const { data, error } = await supabase.from("teams").insert(payload).select("id").single();
      if (error) return toast.error(error.message);
      teamId = data.id;
    }

    await supabase.from("athlete_teams").delete().eq("team_id", teamId!);
    if (editing.athleteIds.length) {
      const rows = editing.athleteIds.map((aid) => ({ team_id: teamId!, athlete_id: aid }));
      const { error } = await supabase.from("athlete_teams").insert(rows);
      if (error) return toast.error(error.message);
    }

    toast.success("Saved");
    setEditing(null);
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this team?")) return;
    const { error } = await supabase.from("teams").delete().eq("id", id);
    if (error) return toast.error(error.message);
    load();
  };

  return (
    <AdminLayout>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-display text-3xl">Teams</h1>
          <p className="text-sm text-muted-foreground mt-1">{list.length} total</p>
        </div>
        <button
          onClick={() => setEditing(empty())}
          className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-secondary transition-colors"
        >
          + New team
        </button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : (
        <div className="grid gap-3">
          {list.map((t) => (
            <div key={t.id} className="bg-card rounded-2xl p-4 flex items-center gap-4 shadow-card">
              <div className="w-16 h-16 rounded-xl bg-muted overflow-hidden shrink-0 flex items-center justify-center">
                {t.logo_path ? (
                  <img src={publicUrl(t.logo_path)!} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-display text-xl text-muted-foreground">{t.name.charAt(0)}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold truncate">{t.name}</p>
                  <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${t.published ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                    {t.published ? "Live" : "Draft"}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t.sport ?? "—"} · {(links[t.id] ?? []).length} athlete{(links[t.id] ?? []).length === 1 ? "" : "s"}
                </p>
              </div>
              <button onClick={() => startEdit(t)} className="px-4 py-2 rounded-full bg-muted text-sm font-semibold">Edit</button>
              <button onClick={() => remove(t.id)} className="px-4 py-2 rounded-full text-sm font-semibold text-destructive hover:bg-destructive/10">Delete</button>
            </div>
          ))}
          {list.length === 0 && <p className="text-muted-foreground">No teams yet. Create your first one.</p>}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-card rounded-[1.5rem] shadow-bold max-w-2xl w-full p-6 my-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display text-2xl">{editing.id ? "Edit" : "New"} team</h2>
              <button onClick={() => setEditing(null)} className="text-muted-foreground hover:text-foreground">✕</button>
            </div>

            <div className="space-y-4">
              <Field label="Logo">
                <ImageUploader
                  value={editing.logo_path ?? null}
                  onChange={(p) => setEditing({ ...editing, logo_path: p })}
                  prefix="team-logos"
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Name *">
                  <input value={editing.name ?? ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inp} maxLength={120} />
                </Field>
                <Field label="Slug (URL)">
                  <input value={editing.slug ?? ""} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder={slugify(editing.name ?? "")} className={inp} maxLength={120} />
                </Field>
                <Field label="Sport">
                  <input value={editing.sport ?? ""} onChange={(e) => setEditing({ ...editing, sport: e.target.value })} className={inp} maxLength={50} />
                </Field>
                <Field label="Sort order">
                  <input type="number" value={editing.sort_order ?? 0} onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value) || 0 })} className={inp} />
                </Field>
              </div>

              <Field label="Description">
                <RichTextEditor value={editing.description ?? ""} onChange={(v) => setEditing({ ...editing, description: v })} />
              </Field>

              <Field label={`Gallery (3–5 images) — ${(editing.image_paths ?? []).length}/5`}>
                <GalleryUploader
                  value={editing.image_paths ?? []}
                  onChange={(paths) => {
                    if (paths.length > 5) { toast.error("Max 5 images"); return; }
                    setEditing({ ...editing, image_paths: paths });
                  }}
                  prefix="teams"
                />
              </Field>

              <Field label="Video URL (YouTube, Vimeo, or direct .mp4)">
                <input value={editing.video_url ?? ""} onChange={(e) => setEditing({ ...editing, video_url: e.target.value })} className={inp} placeholder="https://youtube.com/..." />
              </Field>

              <div className="rounded-xl bg-muted/50 border border-border p-4 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider">Links (all optional)</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Website">
                    <input value={editing.website_url ?? ""} onChange={(e) => setEditing({ ...editing, website_url: e.target.value })} className={inp} placeholder="https://" />
                  </Field>
                  <Field label="Tickets">
                    <input value={editing.tickets_url ?? ""} onChange={(e) => setEditing({ ...editing, tickets_url: e.target.value })} className={inp} placeholder="https://" />
                  </Field>
                  <Field label="Shop URL (Shop Our Gear button)">
                    <input value={editing.shop_url ?? ""} onChange={(e) => setEditing({ ...editing, shop_url: e.target.value })} className={inp} placeholder="https://" />
                  </Field>
                  <Field label="Instagram">
                    <input value={editing.instagram_url ?? ""} onChange={(e) => setEditing({ ...editing, instagram_url: e.target.value })} className={inp} placeholder="https://instagram.com/..." />
                  </Field>
                  <Field label="TikTok">
                    <input value={editing.tiktok_url ?? ""} onChange={(e) => setEditing({ ...editing, tiktok_url: e.target.value })} className={inp} placeholder="https://tiktok.com/@..." />
                  </Field>
                  <Field label="YouTube">
                    <input value={editing.youtube_url ?? ""} onChange={(e) => setEditing({ ...editing, youtube_url: e.target.value })} className={inp} placeholder="https://youtube.com/@..." />
                  </Field>
                </div>
              </div>

              <Field label="Athletes on this team">
                <MultiAthletePicker
                  athletes={athletes}
                  selected={editing.athleteIds}
                  onChange={(ids) => setEditing({ ...editing, athleteIds: ids })}
                />
              </Field>

              <label className="flex items-center gap-3 px-4 py-3 rounded-xl bg-muted cursor-pointer">
                <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="w-5 h-5 accent-primary" />
                <span className="font-semibold text-sm">Published (visible on site)</span>
              </label>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full bg-muted font-semibold">Cancel</button>
              <button onClick={save} className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-secondary transition-colors">Save</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

const inp = "w-full px-3 py-2 rounded-xl bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-primary text-sm";

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wider">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

export default AdminTeams;
